<template>
  <main
    id="create-item-page"
    class="page-container"
    v-if="$route.name === 'listini-add-item'"
  >
    <button type="button" class="back-btn" @click="goBack">
      <i class="fa-solid fa-arrow-left"></i> Indietro
    </button>
    <form class="create-item-form" @submit.prevent="onSubmit">
      <!-- ── Campi comuni ─────────────────────────────────────────── -->
      <!-- [sc-48583 / task 48599] La scheda è una macro; l'articolo nasce con
           una merceologia. Quando la scheda ne raccoglie una sola non c'è
           niente da chiedere e il campo non compare. -->
      <div class="create-item-row" v-if="merceologie.length > 1">
        <!-- [sc-46314] Ancore del riflettore: il buco illumina questo
             contenitore, etichetta compresa. Se il campo non c'è - qui la
             merceologia unica lo nasconde - il riflettore salta il passo. -->
        <div class="field" data-tour="item-category">
          <label for="item-category">Tipo di articolo *</label>
          <StatusSelect
            v-model="form.category"
            :options="merceologieOptions"
            placeholder="Scegli..."
            placeholderIcon="fa-layer-group"
          />
          <small style="color: #718096; font-size: 0.75rem">
            Decide quali campi ti vengono chiesti qui sotto.
          </small>
        </div>
      </div>

      <div class="create-item-row">
        <div class="field">
          <label for="item-code">Codice articolo</label>
          <input
            id="item-code"
            type="text"
            v-model.trim="form.itemCode"
            placeholder="es. ART-001"
            maxlength="255"
          />
          <small style="color: #718096; font-size: 0.75rem"
            >Facoltativo: può essere lasciato vuoto.</small
          >
        </div>
        <div class="field" data-tour="item-name">
          <label for="item-name">Nome articolo *</label>
          <input
            id="item-name"
            type="text"
            v-model.trim="form.itemName"
            placeholder="inserisci"
            required
          />
        </div>
      </div>

      <div class="create-item-row">
        <div class="field">
          <label for="brand">Brand</label>
          <input
            id="brand"
            type="text"
            v-model="form.brand"
            placeholder="inserisci"
          />
        </div>
        <div class="field">
          <label for="serie">Serie</label>
          <input
            id="serie"
            type="text"
            v-model="form.serie"
            placeholder="inserisci"
          />
        </div>
        <div
          class="field"
          data-tour="item-price"
          v-if="category !== 'piastrelle'"
        >
          <label for="price">{{ priceLabel }} — IVA esclusa</label>
          <input
            id="price"
            type="number"
            step="any"
            min="0"
            v-model.number="form.price"
            placeholder="inserisci"
          />
        </div>
        <div class="field" data-tour="item-sales-unit">
          <label for="sales-unit">Unità di vendita *</label>
          <StatusSelect
            v-model="form.salesUnit"
            :options="salesUnitOptions"
            placeholder="Seleziona unità..."
            placeholderIcon="fa-ruler-combined"
          />
        </div>
        <div class="field">
          <label for="articleStatus">Stato</label>
          <StatusSelect
            v-model="form.articleStatus"
            :options="articleStatusOptions"
          />
        </div>
      </div>

      <hr />

      <!-- [sc-48351] I campi della categoria, gli stessi della modale che
           crea un articolo dal preventivo. -->
      <ItemCategoryFields :form="form" :category="category" />

      <hr />

      <div class="save-button-container" data-tour="item-save">
        <button
          id="create-item-button"
          class="primary-btn"
          type="submit"
          :disabled="!canSave"
          :aria-disabled="!canSave"
          :title="!canSave ? 'Inserisci il nome articolo' : 'Salva articolo'"
        >
          Salva
        </button>
      </div>
    </form>

    <VueFinalModal
      v-model="showBackModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showBackModal = false"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Tornare indietro?</h3>
        <p class="modal__subtitle">Le modifiche non salvate andranno perse.</p>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showBackModal = false">
            Annulla
          </button>
          <button class="primary-btn confirm-btn" @click="confirmGoBack">
            Conferma
          </button>
        </div>
      </template>
    </VueFinalModal>

    <OnboardingSpotlight />
  </main>
</template>

<script>
import { mapActions } from "vuex/dist/vuex.cjs.js";
import StatusSelect from "@/components/StatusSelect.vue";
import OnboardingSpotlight from "@/components/OnboardingSpotlight.vue";
import { salesUnitsFor } from "@/config/catalog";
import { itemCategoriesOf } from "@/config/listiniCategories";
import ItemCategoryFields from "@/components/ItemCategoryFields.vue";
import { priceLabelFor } from "@/config/itemFields";

export default {
  name: "CreateItemPage",
  components: { StatusSelect, OnboardingSpotlight, ItemCategoryFields },
  beforeRouteLeave(to) {
    if (
      this.navigationConfirmed ||
      to.name === "login" ||
      !this.hasUnsavedChanges
    ) {
      this.navigationConfirmed = false;
      return true;
    }
    this.pendingNavigation = to;
    this.showBackModal = true;
    return false;
  },
  data() {
    return {
      showBackModal: false,
      pendingNavigation: null,
      navigationConfirmed: false,
      pristineFormSig: "", // snapshot del form iniziale, per rilevare modifiche
      articleStatusOptions: [
        { value: "attivo", label: "Attivo", color: "#059669" },
        { value: "novita", label: "Novità", color: "#2563eb" },
        { value: "discontinuo", label: "Discontinuo", color: "#9ca3af" },
      ],
      form: {
        // La merceologia: la sceglie la tendina qui sopra, o la scheda quando
        // ne raccoglie una sola.
        category: "",
        itemCode: "",
        itemName: "",
        brand: "",
        serie: "",
        price: 0,
        salesUnit: "",
        articleStatus: "attivo",
        subCategory: "",
        formatLabel: "",
        thicknessMm: null,
        surface: "",
        color: "",
        piecesPerBox: null,
        sqmPerBox: null,
        boxQuantity: null,
        fixtureType: "",
        fixturePosition: "",
        finish: "",
        material: "",
        lengthMm: null,
        mlPerBox: null,
        widthCm: null,
        heightCm: null,
        specs: { scelta: "" },
        notes: "",
      },
    };
  },
  computed: {
    // true se il form differisce dallo snapshot iniziale (dati inseriti).
    hasUnsavedChanges() {
      if (!this.pristineFormSig) return false;
      return JSON.stringify(this.form) !== this.pristineFormSig;
    },
    /** La scheda da cui si è arrivati: è una macro, non una merceologia. */
    macro() {
      return decodeURIComponent(this.$route.params.category || "");
    },
    /** Le merceologie che quella scheda raccoglie. */
    merceologie() {
      return itemCategoriesOf(this.macro);
    },
    /**
     * Le merceologie nella forma che vuole `StatusSelect`.
     *
     * [sc-48583] Era l'ultima tendina nativa della scheda: in mezzo a otto
     * campi che si aprono tutti allo stesso modo, quella si apriva col menu
     * del sistema operativo e sembrava un pezzo di un'altra pagina.
     */
    merceologieOptions() {
      return this.merceologie.map((voce) => ({
        value: voce,
        label: this.etichettaMerceologia(voce),
      }));
    },
    /**
     * La merceologia dell'articolo che si sta creando.
     *
     * Da qui in giù non cambia niente: tutta la scheda ragiona sulla
     * merceologia, come prima, e prima arrivava dalla rotta perché scheda e
     * merceologia coincidevano.
     */
    category() {
      return this.form.category;
    },
    salesUnitOptions() {
      // Solo le unità scelte in configurazione iniziale. Se non ce ne sono —
      // istanza mai configurata — `salesUnitsFor` le restituisce tutte, così
      // la tendina non resta vuota e la scheda inutilizzabile.
      return salesUnitsFor(this.$store.state.companyProfileUnits);
    },
    defaultSalesUnit() {
      // Con una sola unità non c'è niente da scegliere: si preseleziona, così
      // il campo obbligatorio non diventa un ostacolo inutile.
      return this.salesUnitOptions.length === 1
        ? this.salesUnitOptions[0].value
        : "";
    },
    priceLabel() {
      // [sc-48350 / task 48396] Legge il campo scelto, non più una deduzione
      // dalla categoria: l'unità è un dato dell'articolo, non della sua
      // tipologia.
      return priceLabelFor(this.form.salesUnit);
    },
    canSave() {
      if (!(this.form.itemName || "").trim()) return false;
      // Il `required` della tendina nativa se n'e' andato con la tendina: il
      // campo resta obbligatorio, e a dirlo ora e' il pulsante spento.
      if (!this.category) return false;
      if (!this.form.salesUnit) return false;
      if (this.category === "piastrelle" && !this.form.subCategory)
        return false;
      if (this.category === "bagno" && !this.form.subCategory) return false;
      return true;
    },
  },
  methods: {
    /** «rivestimenti parete» in tendina si legge meglio con la maiuscola. */
    etichettaMerceologia(valore) {
      const testo = String(valore || "");
      return testo.charAt(0).toUpperCase() + testo.slice(1);
    },
    /**
     * Con una merceologia sola non c'è niente da scegliere: si preseleziona,
     * così un campo obbligatorio non diventa un ostacolo inutile. È la stessa
     * regola già usata per l'unità di vendita.
     */
    preselezionaMerceologia() {
      if (this.form.category) return;
      if (this.merceologie.length === 1) {
        this.form.category = this.merceologie[0];
      }
    },
    goBack() {
      this.pendingNavigation = null;
      // Nessun dato inserito: torna indietro senza conferma.
      if (!this.hasUnsavedChanges) {
        this.navigationConfirmed = true;
        this.$router.back();
        return;
      }
      this.showBackModal = true;
    },
    // Guardia reload/chiusura tab nativa del browser: dialog nativo di
    // conferma solo se ci sono dati non salvati.
    handleBeforeUnload(e) {
      if (!this.hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = "";
    },
    confirmGoBack() {
      this.navigationConfirmed = true;
      this.showBackModal = false;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
      } else {
        this.$router.back();
      }
    },
    ...mapActions(["createItem"]),

    resetForm() {
      this.form = {
        // Si resta nella stessa scheda: la merceologia scelta vale anche per
        // l'articolo successivo, e ripeterla a ogni riga sarebbe fatica inutile.
        category: this.form.category,
        itemCode: "",
        itemName: "",
        brand: "",
        serie: "",
        price: 0,
        // Ripristinare non deve rimettere in mezzo una scelta che non c'è:
        // con una sola unità resta preselezionata come al primo caricamento.
        salesUnit: this.defaultSalesUnit,
        articleStatus: "attivo",
        subCategory: "",
        formatLabel: "",
        thicknessMm: null,
        surface: "",
        color: "",
        piecesPerBox: null,
        sqmPerBox: null,
        boxQuantity: null,
        fixtureType: "",
        fixturePosition: "",
        finish: "",
        material: "",
        lengthMm: null,
        mlPerBox: null,
        widthCm: null,
        heightCm: null,
        specs: { scelta: "" },
        notes: "",
      };
    },

    async onSubmit() {
      const payload = {
        ...this.form,
        category: this.category,
      };

      // la giacenza si gestisce dal magazzino (modale in MagazzinoPage), non
      // dalla scheda articolo
      delete payload.boxQuantity;

      if (this.category === "parquet") {
        payload.specs = this.form.specs?.scelta
          ? { scelta: this.form.specs.scelta }
          : null;
      } else {
        delete payload.specs;
      }

      try {
        const res = await this.createItem(payload);
        this.$toast.success(res.data?.message || "Articolo creato!");
        this.resetForm();
        setTimeout(() => {
          this.navigationConfirmed = true;
          this.$router.push({
            name: "listini-category",
            params: { category: this.macro },
          });
        }, 800);
      } catch (err) {
        if (
          err.response?.status === 409 &&
          err.response?.data?.code === "ITEM_CODE_DUPLICATE"
        ) {
          this.$toast.error(
            "Questo codice è già utilizzato da un altro articolo."
          );
        } else {
          this.$toast.error(
            err.response?.data?.message ||
              err.response?.data?.error ||
              "Errore durante la creazione."
          );
        }
      }
    },
  },
  mounted() {
    const sub = this.$route.query.subCategory;
    if (sub) this.form.subCategory = sub;
    this.preselezionaMerceologia();
    this.form.salesUnit = this.defaultSalesUnit;
    // Snapshot dopo l'eventuale precompilazione da query string
    this.pristineFormSig = JSON.stringify(this.form);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
};
</script>

<style src="@/css/CreateItemPage.css"></style>
